import React from 'react'
import { Box, Checkbox, Chip, ListItemText, MenuItem, TextField, Typography } from '@mui/material'

import { AppOfficeGroupsAutocomplete, AppOfficesAutocomplete, AppSiteGroupsAutocomplete, AppSitesAutocomplete } from '@components'
import { SERVICES_TYPE } from '@constants'
import { dsColorNew } from '@palette'
import KindPillsField from '../../../@components/KindPillsField'
import { FieldNote } from '../../../@components/ViewBand'
import { kindPills, menuTab, replaceNote } from '../../../@services/kindPills'
import { DateRangePickerSeeker } from '@ui-seeker/core'
import { addDays, daysBetween, fmtRange, fmtWeekday, toIso } from '../../../@services/dates'
import { MAX_CAPTURE_DAYS } from '../@services/capture'
import { TYPE_LABEL } from '../@services/groups'
import { MAX_LENGTHS, OFFICE_TYPES } from '../@services/viewState'


// decision 10: groups are each user's own
const groupHeader = (where, onOpen) => <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: 1 }}>
	Your groups · <Box component="button" type="button" onClick={onOpen} sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: dsColorNew.appBlue, font: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit' }}>Settings › {where}</Box>
</Typography>
const ac = { multiple: true, limitTags: 3, fullWidth: true, openOnFocus: true, ListboxProps: { style: { maxHeight: 260 } } }

function FacetChips({ title, items, selected, onToggle }) {
	return <Box sx={{ mb: 1.25 }}>
		<Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle, mb: 0.75 }}>{title}</Typography>
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
			{items.map(i => <Chip key={i.value} size="small" clickable={!i.disabled} disabled={i.disabled} title={i.disabled ? i.reason : undefined}
				label={`${i.label} ${i.count}`} color={selected.includes(i.value) ? 'primary' : 'default'} variant={selected.includes(i.value) ? 'filled' : 'outlined'}
				onClick={() => !i.disabled && onToggle(i.value)}/>)}
		</Box>
	</Box>
}

/**
 * "Offices" (CA 16, decision 1, 10): the whole network, or one kind at a time — offices by hand, or one of your office
 * groups — with Country and Office type as facets above the pills, with counts. Before the load: it asks again.
 */
export function OfficesField({ view, network, groupName = null, onChange, onOpenSettings }) {
	const kind = view.officeGroup ? 'group' : 'ids'
	const on = !!view.officeGroup || !!view.offices.length || !!view.countries.length || !!view.types.length
	const countries = Object.values(network.reduce((acc, o) => {
		const k = o.country || '—'
		acc[k] = acc[k] || { value: k, label: o.countryName || k, count: 0 }
		acc[k].count += 1
		return acc
	}, {})).sort((a, b) => String(a.label).localeCompare(String(b.label)))
	const types = OFFICE_TYPES.map(t => {
		const count = network.filter(o => (o.type || 'other') === t).length
		return { value: t, label: TYPE_LABEL[t], count, disabled: !count, reason: 'No office of this type in your network' }
	})
	const toggle = (list, v) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v])
	const labels = [
		...(view.officeGroup ? [groupName || 'Office group'] : view.offices.length ? [`${view.offices.length} offices`] : []),
		...view.countries, ...view.types.map(t => TYPE_LABEL[t]),
	]
	const facetsText = [view.countries.join(', '), view.types.map(t => TYPE_LABEL[t]).join(', ')].filter(Boolean).join(', ')
	const counted = network.length ? `${network.length} offices · ${countries.length} ${countries.length === 1 ? 'country' : 'countries'}` : null
	const base = view.officeGroup ? `Office group${groupName ? ` · ${groupName}` : ''}` : view.offices.length ? `${view.offices.length} offices` : counted || 'Whole network'
	return <KindPillsField
		label="Offices" allText="Whole network" clearLabel="Whole network" pillsLabel="Offices by"
		on={on} labels={labels} under={`${base}${facetsText ? ` · ${facetsText}` : ''}`}
		kinds={kindPills(['ids', 'group'], { ids: 'Offices', group: 'Office group' })}
		openTab={() => menuTab({ kind, on: !!view.officeGroup || !!view.offices.length }, 'ids')}
		noteFor={tab => replaceNote({ kind, on: !!view.officeGroup || !!view.offices.length }, tab, labels)}
		facets={<>
			<FacetChips title="Country" items={countries} selected={view.countries} onToggle={c => onChange({ countries: toggle(view.countries, c) })}/>
			<FacetChips title="Office type" items={types} selected={view.types} onToggle={t => onChange({ types: toggle(view.types, t) })}/>
		</>}
		renderKind={(tab, close) => (tab === 'group'
			? <>
				{groupHeader('Office group', () => { close(); onOpenSettings() })}
				<AppOfficeGroupsAutocomplete {...ac} multiple={false} value={view.officeGroup} placeholder="One of your office groups"
					onChange={e => onChange({ officeGroup: e.target.value ? Number(e.target.value) : null, offices: [] })}/>
			</>
			: <AppOfficesAutocomplete {...ac} value={view.offices} filters={{ service_type: SERVICES_TYPE.BI }} placeholder="Whole network"
				onChange={e => onChange({ offices: (e.target.value || []).map(Number), officeGroup: null })}/>)}
		onClear={() => onChange({ offices: [], officeGroup: null, countries: [], types: [] })}
	/>
}

/** "Sites" (CA 22, decision 3): all websites by default, some by hand, or one of your site groups */
export function SitesField({ view, count, groupName = null, onChange, onOpenSettings }) {
	const kind = view.siteGroup ? 'group' : 'ids'
	const on = !!view.siteGroup || !!view.sites.length
	const labels = view.siteGroup ? [groupName || 'Site group'] : view.sites.length ? [`${view.sites.length} websites`] : []
	return <KindPillsField
		label="Sites" allText="All websites" clearLabel="All websites" pillsLabel="Websites by"
		on={on} labels={labels} under={on ? (view.siteGroup ? `Site group${groupName ? ` · ${groupName}` : ''}` : `${view.sites.length} websites`) : `${count ?? 'All'} websites with prices`}
		kinds={kindPills(['ids', 'group'], { ids: 'Website', group: 'Site group' })}
		openTab={() => menuTab({ kind, on }, 'ids')}
		noteFor={tab => replaceNote({ kind, on }, tab, labels)}
		renderKind={(tab, close) => (tab === 'group'
			? <>
				{groupHeader('Site group', () => { close(); onOpenSettings() })}
				<AppSiteGroupsAutocomplete {...ac} multiple={false} value={view.siteGroup} placeholder="One of your site groups"
					onChange={e => onChange({ siteGroup: e.target.value ? Number(e.target.value) : null, sites: [] })}/>
			</>
			: <AppSitesAutocomplete {...ac} value={view.sites} filters={{ service_type: SERVICES_TYPE.BI }} placeholder="All websites"
				onChange={e => onChange({ sites: (e.target.value || []).map(Number), siteGroup: null })}/>)}
		onClear={() => onChange({ sites: [], siteGroup: null })}
	/>
}

/** "Rental lengths" (several, the lengths view) or "Rental length" (one, the other views) */
export function LengthsField({ view, options, shown, withoutPrices = [], onChange }) {
	const values = options.map(o => Number(o.value))
	if (view.v !== 'length') {
		return <Box>
			<TextField select size="small" fullWidth label="Rental length" value={values.includes(view.lor) ? view.lor : ''} onChange={e => onChange({ lor: Number(e.target.value) })}>
				{values.map(v => <MenuItem key={v} value={v}>{v} {v === 1 ? 'day' : 'days'}</MenuItem>)}
			</TextField>
			<FieldNote>One length in this view</FieldNote>
		</Box>
	}
	// a field with the lengths chosen and a menu, as in the mock (QA 5); never a row of every length
	const pick = next => { if (next.length) onChange({ lens: [...next].map(Number).sort((a, b) => a - b) }) }
	const text = shown.length ? `${shown.join(', ')} days` : 'Choose rental lengths'
	return <Box>
		<TextField select size="small" fullWidth label="Rental lengths" value={shown}
			SelectProps={{
				multiple: true, renderValue: () => text,
				MenuProps: { PaperProps: { sx: { maxHeight: 360 } } },
			}}
			onChange={e => pick(Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(','))}>
			{values.map(v => {
				const on = shown.includes(v)
				const full = !on && shown.length >= MAX_LENGTHS
				return <MenuItem key={v} value={v} disabled={full} dense>
					<Checkbox size="small" checked={on} sx={{ p: 0.5, mr: 1 }}/>
					<ListItemText primary={`${v} ${v === 1 ? 'day' : 'days'}`} secondary={full ? `Up to ${MAX_LENGTHS} rental lengths at a time` : (withoutPrices.includes(v) ? 'no prices' : null)}
						secondaryTypographyProps={{ fontSize: 12 }}/>
				</MenuItem>
			})}
		</TextField>
		<FieldNote>{shown.length} column{shown.length === 1 ? '' : 's'}{withoutPrices.length ? ` · ${withoutPrices.join(', ')} days: no prices` : ''}</FieldNote>
	</Box>
}

/** A select whose options that do not apply stay, disabled with their reason */
function ReasonSelect({ label, value, options, onChange, under }) {
	return <Box>
		<TextField select size="small" fullWidth label={label} value={value} onChange={e => onChange(e.target.value)}
			SelectProps={{ renderValue: v => options.find(o => o.value === v)?.label || '' }}>
			{options.map(o => <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
				<ListItemText primary={o.label} secondary={o.disabled ? o.reason : o.hint} secondaryTypographyProps={{ fontSize: 12 }}/>
			</MenuItem>)}
		</TextField>
		{!!under && <FieldNote>{under}</FieldNote>}
	</Box>
}

/** "Prices captured" (CA 22, decision 9) and the day(s) it resolves to; Fixed dates with the classic date range picker */
export function CapturedField({ value, options, capture, maxDays = MAX_CAPTURE_DAYS, onChange, onFixed }) {
	const under = capture ? (capture.days === 1 ? fmtWeekday(capture.from) : fmtRange(capture.from, capture.to)) : null
	return <Box>
		<ReasonSelect label="Prices captured" value={value} options={options.map(o => (o.value === 'fixed' ? { ...o, label: 'Fixed dates…', hint: `Up to ${maxDays} days` } : o))} onChange={onChange} under={value === 'fixed' ? null : under}/>
		{value === 'fixed' && <Box sx={{ mt: 1 }}>
			<DateRangePickerSeeker
				label="Capture dates"
				value={capture ? [capture.from, capture.to] : null}
				onChange={dates => {
					const [a, z] = Array.isArray(dates) ? dates : []
					if (!(a instanceof Date) || !(z instanceof Date)) return
					// the server takes up to 31 capture days (decision 9), fewer for a large network (the cost bound)
					const from = daysBetween(a, z) + 1 > maxDays ? addDays(z, -(maxDays - 1)) : a
					onFixed({ from: toIso(from), to: toIso(z) })
				}}
			/>
			{!!under && <FieldNote>{under}</FieldNote>}
		</Box>}
	</Box>
}

/** "Compare with" (CA 18, P1) and the resolved comparison date */
export function CompareField({ value, options, under, onChange }) {
	return <ReasonSelect label="Compare with" value={value} options={options} onChange={onChange} under={under}/>
}
